import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cloud, Server, Shield, Loader2, CheckCircle2, Play, Settings, Info,
  AlertTriangle, AlertCircle, XCircle, History, Trash2, FlaskConical,
  ChevronRight, ExternalLink, Save, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const providerIcon = { aws: Cloud, azure: Cloud, gcp: Cloud, github: Server, okta: Shield };
const SEVERITY_COLORS = {
  critical: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
  high:     "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30",
  medium:   "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  low:      "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
  info:     "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
};
const STATUS_MAP = {
  compliant: { icon: CheckCircle2, color: "text-emerald-500", label: { es: "Cumple", en: "Compliant" } },
  non_compliant: { icon: XCircle, color: "text-red-500", label: { es: "No cumple", en: "Non-compliant" } },
  warning: { icon: AlertTriangle, color: "text-amber-500", label: { es: "Advertencia", en: "Warning" } },
};

const EvidenceCollection = () => {
  const { token, user } = useAuth();
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const isES = locale === "es";
  const isOwner = user?.role === "owner";

  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]); // [{provider, config, updated_at}]
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [configDialog, setConfigDialog] = useState(null); // provider meta
  const [configForm, setConfigForm] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [testingCfg, setTestingCfg] = useState(false);

  const [running, setRunning] = useState(""); // provider id currently scanning
  const [scanDetail, setScanDetail] = useState(null); // { scan, findings }

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, cRes, sRes] = await Promise.all([
        axios.get(`${API}/cloud-scanners/providers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cloud-scanners/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cloud-scanners/scans`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProviders(pRes.data.providers || []);
      setConfigs(cRes.data.configs || []);
      setScans(sRes.data || []);
    } catch (e) {
      toast.error(t("ec.load_failed"));
    } finally { setLoading(false); }
  }, [token, isES]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getConfig = (pid) => configs.find((c) => c.provider === pid);
  const getLatestScan = (pid) => scans.find((s) => s.provider === pid);

  // --- Config dialog ---
  const openConfigDialog = (p) => {
    setConfigDialog(p);
    const existing = getConfig(p.id)?.config || {};
    const seed = {};
    for (const f of p.fields) {
      if (f.default !== undefined) seed[f.key] = f.default;
      // prefill non-secret existing values
      if (existing[f.key] && !["secret_access_key", "client_secret", "service_account_json",
                                "personal_access_token", "api_token"].includes(f.key)) {
        seed[f.key] = existing[f.key];
      }
    }
    setConfigForm(seed);
  };

  const saveConfig = async () => {
    const meta = configDialog;
    if (!meta) return;
    for (const f of meta.fields) {
      if (f.required && !configForm[f.key]) {
        const existing = getConfig(meta.id)?.config || {};
        if (existing[`${f.key}_set`]) continue;
        toast.error(`${f.label} ${t("ec.field_required")}`);
        return;
      }
    }
    setSavingCfg(true);
    try {
      const payload = {};
      Object.keys(configForm).forEach((k) => {
        if (configForm[k] !== "" && configForm[k] !== undefined) payload[k] = configForm[k];
      });
      await axios.put(`${API}/cloud-scanners/config/${meta.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("ec.creds_saved"));
      await fetchAll();
      setConfigDialog(null);
    } catch (e) {
      toast.error(extractError(e, t("ec.save_failed")));
    } finally { setSavingCfg(false); }
  };

  const testConfig = async (pid) => {
    setTestingCfg(true);
    try {
      const res = await axios.post(`${API}/cloud-scanners/test/${pid}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (pid === "aws") {
        toast.success(`${t("ec.aws_ok")} ${res.data.account}`);
      } else {
        toast.success(res.data.note || t("ec.creds_saved"));
      }
    } catch (e) {
      toast.error(extractError(e, t("ec.validation_failed")));
    } finally { setTestingCfg(false); }
  };

  const deleteConfig = async (pid) => {
    if (!(await confirm({ title: t("ec.delete_title"), description: t("ec.delete_confirm"), destructive: true }))) return;
    try {
      await axios.delete(`${API}/cloud-scanners/config/${pid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("ec.deleted"));
      await fetchAll();
    } catch {
      toast.error(t("ec.delete_failed"));
    }
  };

  const runScan = async (pid) => {
    setRunning(pid);
    try {
      const res = await axios.post(`${API}/cloud-scanners/scan/${pid}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const mode = res.data.scan.mode;
      const counts = res.data.scan.counts;
      toast.success(isES
        ? `Escaneo ${mode.toUpperCase()} completado: ${counts.compliant} OK, ${counts.non_compliant} fallos, ${counts.warning} avisos`
        : `Scan ${mode.toUpperCase()} complete: ${counts.compliant} pass, ${counts.non_compliant} fail, ${counts.warning} warn`);
      if (res.data.scan.error) {
        toast.warning(res.data.scan.error);
      }
      await fetchAll();
      // Open scan detail right away
      openScanDetail(res.data.scan.scan_id);
    } catch (e) {
      toast.error(extractError(e, t("ec.scan_failed")));
    } finally { setRunning(""); }
  };

  const openScanDetail = async (scanId) => {
    try {
      const res = await axios.get(`${API}/cloud-scanners/scans/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScanDetail(res.data);
    } catch {
      toast.error(t("ec.detail_failed"));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const totalConfigured = configs.length;
  const totalScans = scans.length;
  const totalFindings = scans.reduce((s, x) => s + (x.counts?.total || 0), 0);
  const totalNonCompliant = scans.reduce((s, x) => s + (x.counts?.non_compliant || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="evidence-collection-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Cloud className="w-8 h-8 text-primary" />
            {t("ec.page_title")}
          </span>
        }
        description={t("ec.page_desc")}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold">{providers.length}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("ec.providers_label")}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold text-primary">{totalConfigured}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("ec.connected_label")}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold text-blue-500">{totalScans}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("ec.scans_label")}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold text-red-500">{totalNonCompliant}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("ec.findings_label")}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="connectors" className="w-full">
        <TabsList>
          <TabsTrigger value="connectors" data-testid="tab-connectors">
            {t("ec.tab_connectors")}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-1" />
            {t("ec.tab_history")} ({totalScans})
          </TabsTrigger>
        </TabsList>

        {/* Connectors tab */}
        <TabsContent value="connectors" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((p) => {
              const Icon = providerIcon[p.id] || Cloud;
              const cfg = getConfig(p.id);
              const last = getLatestScan(p.id);
              return (
                <motion.div key={p.id} whileHover={{ y: -2 }} className="h-full">
                  <Card className="h-full border-border" data-testid={`connector-${p.id}`}>
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{p.name}</h3>
                            {cfg && (
                              <Badge variant="outline" className="text-[10px] bg-primary/15 text-primary border-primary/30">
                                {t("ec.badge_connected")}
                              </Badge>
                            )}
                            {p.real_scan ? (
                              <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/30">
                                {t("ec.badge_live")}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">
                                MOCK
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-[11px] font-medium text-muted-foreground mb-1">
                          {t("ec.evidence_types")}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {p.evidence_types.slice(0, 4).map((e, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">{e}</Badge>
                          ))}
                          {p.evidence_types.length > 4 && (
                            <Badge variant="secondary" className="text-[10px]">+{p.evidence_types.length - 4}</Badge>
                          )}
                        </div>
                      </div>

                      {last && (
                        <button
                          className="w-full text-left bg-muted/40 hover:bg-muted/60 rounded p-2 text-xs transition"
                          onClick={() => openScanDetail(last.scan_id)}
                          data-testid={`view-last-scan-${p.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {t("ec.last_scan")}
                            </span>
                            <ChevronRight className="w-3 h-3" />
                          </div>
                          <div className="flex gap-2 mt-1">
                            <span className="text-emerald-600">✓ {last.counts?.compliant || 0}</span>
                            <span className="text-red-600">✗ {last.counts?.non_compliant || 0}</span>
                            <span className="text-amber-600">⚠ {last.counts?.warning || 0}</span>
                            <span className="text-muted-foreground ml-auto">{last.mode?.toUpperCase()}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {new Date(last.started_at).toLocaleString()}
                          </div>
                        </button>
                      )}

                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          variant={cfg ? "outline" : "default"}
                          className="flex-1"
                          onClick={() => openConfigDialog(p)}
                          disabled={!isOwner}
                          data-testid={`configure-${p.id}`}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          {cfg ? t("ec.btn_edit") : t("ec.btn_connect")}
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => runScan(p.id)}
                          disabled={running === p.id}
                          data-testid={`scan-${p.id}`}
                        >
                          {running === p.id
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Play className="w-4 h-4 mr-1" />}
                          {running === p.id
                            ? t("ec.btn_scanning")
                            : t("ec.btn_scan")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </TabsContent>

        {/* Scan history tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {scans.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  {t("ec.no_scans")}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {scans.map((s) => (
                    <button
                      key={s.scan_id}
                      className="w-full py-3 flex items-center gap-3 hover:bg-muted/40 px-2 rounded text-left"
                      onClick={() => openScanDetail(s.scan_id)}
                      data-testid={`history-row-${s.scan_id}`}
                    >
                      <div className="w-9 h-9 rounded bg-primary/10 flex items-center justify-center shrink-0">
                        {(providerIcon[s.provider] || Cloud) && (() => {
                          const I = providerIcon[s.provider] || Cloud;
                          return <I className="w-4 h-4 text-primary" />;
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium capitalize">{s.provider}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {s.mode?.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(s.started_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex gap-3 text-xs mt-0.5">
                          <span className="text-emerald-600">✓ {s.counts?.compliant || 0}</span>
                          <span className="text-red-600">✗ {s.counts?.non_compliant || 0}</span>
                          <span className="text-amber-600">⚠ {s.counts?.warning || 0}</span>
                          <span className="text-muted-foreground">{t("ec.total_label")}: {s.counts?.total || 0}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Config dialog */}
      <Dialog open={!!configDialog} onOpenChange={(o) => !o && setConfigDialog(null)}>
        <DialogContent className="max-w-xl" data-testid="config-dialog">
          {configDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  {configDialog.name}
                </DialogTitle>
                <DialogDescription>
                  {t("ec.creds_desc")}
                </DialogDescription>
              </DialogHeader>

              {!isOwner && (
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs">
                  <Lock className="w-4 h-4 text-amber-500 mt-0.5" />
                  {t("ec.owner_only")}
                </div>
              )}

              <div className="space-y-3">
                {configDialog.fields.map((f) => {
                  const existing = getConfig(configDialog.id)?.config || {};
                  const secretSet = existing[`${f.key}_set`];
                  return (
                    <div key={f.key} className="space-y-1.5">
                      <Label htmlFor={`cs-${f.key}`} className="flex items-center gap-2 text-xs">
                        {f.label}
                        {f.required && <span className="text-destructive">*</span>}
                        {secretSet && (
                          <Badge variant="outline" className="text-[10px]">
                            {t("ec.badge_saved")}
                          </Badge>
                        )}
                      </Label>
                      {f.type === "textarea" ? (
                        <Textarea
                          id={`cs-${f.key}`}
                          rows={5}
                          value={configForm[f.key] || ""}
                          onChange={(e) => setConfigForm({ ...configForm, [f.key]: e.target.value })}
                          disabled={!isOwner}
                          className="font-mono text-xs"
                          placeholder={secretSet ? t("ec.placeholder_set") : ""}
                          data-testid={`cs-field-${f.key}`}
                        />
                      ) : (
                        <Input
                          id={`cs-${f.key}`}
                          type={f.type === "password" ? "password" : "text"}
                          value={configForm[f.key] || ""}
                          onChange={(e) => setConfigForm({ ...configForm, [f.key]: e.target.value })}
                          disabled={!isOwner}
                          placeholder={secretSet ? t("ec.placeholder_set") : (f.default || "")}
                          data-testid={`cs-field-${f.key}`}
                        />
                      )}
                      {f.help && <p className="text-[10px] text-muted-foreground">{f.help}</p>}
                    </div>
                  );
                })}
                <a
                  href={configDialog.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  {t("ec.provider_docs")}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {getConfig(configDialog.id) && isOwner && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteConfig(configDialog.id)}
                    data-testid="cs-delete-btn"
                    className="mr-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {t("ec.btn_delete")}
                  </Button>
                )}
                {getConfig(configDialog.id) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testConfig(configDialog.id)}
                    disabled={testingCfg}
                    data-testid="cs-test-btn"
                  >
                    {testingCfg ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1" />}
                    {t("ec.btn_test")}
                  </Button>
                )}
                <Button onClick={saveConfig} disabled={savingCfg || !isOwner} data-testid="cs-save-btn">
                  {savingCfg ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  {t("ec.btn_save")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Scan detail dialog */}
      <Dialog open={!!scanDetail} onOpenChange={(o) => !o && setScanDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
                       data-testid="scan-detail-dialog">
          {scanDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 capitalize">
                  {(() => {
                    const I = providerIcon[scanDetail.scan.provider] || Cloud;
                    return <I className="w-5 h-5 text-primary" />;
                  })()}
                  {scanDetail.scan.provider} — {t("ec.scan_results")}
                  <Badge variant="outline" className="ml-2">{scanDetail.scan.mode?.toUpperCase()}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {new Date(scanDetail.scan.started_at).toLocaleString()} · {scanDetail.scan.run_by_email}
                </DialogDescription>
              </DialogHeader>

              {scanDetail.scan.error && (
                <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs">
                  <AlertCircle className="inline w-3 h-3 mr-1 text-amber-500" />
                  {scanDetail.scan.error}
                </div>
              )}

              <div className="grid grid-cols-4 gap-2 text-center py-2">
                <div className="bg-emerald-500/10 p-2 rounded">
                  <div className="text-xl font-bold text-emerald-600">{scanDetail.scan.counts?.compliant || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{t("ec.compliant")}</div>
                </div>
                <div className="bg-red-500/10 p-2 rounded">
                  <div className="text-xl font-bold text-red-600">{scanDetail.scan.counts?.non_compliant || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{t("ec.non_compliant")}</div>
                </div>
                <div className="bg-amber-500/10 p-2 rounded">
                  <div className="text-xl font-bold text-amber-600">{scanDetail.scan.counts?.warning || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{t("ec.warnings")}</div>
                </div>
                <div className="bg-primary/5 p-2 rounded">
                  <div className="text-xl font-bold">{scanDetail.scan.counts?.total || 0}</div>
                  <div className="text-[10px] text-muted-foreground">Total</div>
                </div>
              </div>

              <ScrollArea className="flex-1 -mx-6 px-6">
                <div className="space-y-2">
                  {scanDetail.findings.map((f) => {
                    const map = STATUS_MAP[f.status] || STATUS_MAP.warning;
                    const SIcon = map.icon;
                    return (
                      <div
                        key={f.finding_id}
                        className="border border-border rounded p-3 hover:bg-muted/30 transition"
                        data-testid={`finding-${f.finding_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <SIcon className={`w-4 h-4 mt-0.5 shrink-0 ${map.color}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">{f.check}</span>
                              {f.control && (
                                <Badge variant="outline" className="text-[10px]">
                                  {f.control}
                                </Badge>
                              )}
                              {f.severity && (
                                <Badge variant="outline" className={`text-[10px] ${SEVERITY_COLORS[f.severity]}`}>
                                  {f.severity.toUpperCase()}
                                </Badge>
                              )}
                              <span className={`text-[11px] ml-auto ${map.color}`}>
                                {map.label[locale === "es" ? "es" : "en"]}
                              </span>
                            </div>
                            {f.detail && <p className="text-xs text-muted-foreground mt-1">{f.detail}</p>}
                            {f.remediation && (
                              <div className="mt-2 p-2 bg-muted/50 rounded text-[11px] border-l-2 border-blue-500">
                                <strong>{t("ec.remediation")}</strong> {f.remediation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidenceCollection;
